import { InfoOutlined } from '@mui/icons-material';
import { Box, Stack, Tooltip, Typography } from '@mui/material';

import { CloseButton } from '@/ui/components/button/CloseButton';
import { FSCard } from '@/ui/components/FSCard';
import { language } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';
import { Fonts } from '@/ui/theme/fonts';

export function ProjectScoreCard({
  scoreIcon,
  scoreTextColor,
  scoreBackgroundColor,
  scoreTitle,
  resultValue,
  resultUnit,
  resultSecondLine,
  selected,
  infos,
  onClick,
  scoreLetter,
}: {
  scoreIcon: React.ReactNode;
  scoreTextColor: string;
  scoreBackgroundColor: string;
  scoreTitle: string;
  resultValue: number;
  resultUnit: string;
  resultSecondLine: string;
  infos: string;
  selected: boolean;
  onClick?: () => void;
  scoreLetter: string;
}) {
  const cardBackgroundColor = selected
    ? ColorsRaw.anthracite.Warm_Anthracite_2
    : '#160707';
  return (
    <Box sx={{ position: 'relative' }}>
      <FSCard
        sx={{
          padding: 3,
          paddingBottom: 4,
          backgroundColor: cardBackgroundColor,
        }}
        onClick={onClick}
      >
        {/* Score letter + (i) */}
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'start'}
        >
          <Box
            sx={{
              color: scoreTextColor,
              backgroundColor: scoreBackgroundColor,
              mt: 0.5,
              minWidth: 90,
              px: 1,
              width: 'fit-content',
              height: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '18px',
              fontSize: 32,
              fontWeight: 'bold',
              fontFamily: Fonts.DarkerGrotesque,
              '& svg': {
                mr: 1,
              },
            }}
          >
            {scoreIcon}
            {scoreLetter}
          </Box>
          <Tooltip title={infos}>
            <Box sx={{ color: ColorsRaw.anthracite.Anthracite_5 }}>
              <InfoOutlined color="inherit" />
            </Box>
          </Tooltip>
        </Stack>
        {/* Topic name CO2 ENV ESS */}
        <Typography
          color="text.secondary"
          sx={{
            mt: 1.5,
            mb: 1,
            fontSize: 20,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {scoreTitle}
        </Typography>
        {/* Score */}
        <Stack direction={'row'} spacing={0.5} alignItems={'end'}>
          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: 36,
              lineHeight: 1,
              fontFamily: Fonts.DarkerGrotesque,
            }}
          >
            {formatResultValue(resultValue)}
          </Typography>
          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: 20,
              lineHeight: 1,
              fontFamily: Fonts.DarkerGrotesque,
            }}
          >
            {resultUnit}
          </Typography>
        </Stack>
        <Box sx={{ mt: 1 }}>
          <Typography color="text.secondary">
            {resultSecondLine || '\u00A0'}
            {/* \u00A0 is a invisible character to ensure that typography keeps its height with blank text */}
          </Typography>
        </Box>
      </FSCard>
      <Box sx={{ position: 'absolute', top: -10, right: -10 }}>
        {selected && <CloseButton onClick={onClick} />}
      </Box>
    </Box>
  );
}

function formatResultValue(value: number) {
  if (value === Infinity) {
    return '_';
  }
  return value.toLocaleString(language) || '-';
}
